import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiDownload, FiPrinter, FiRotateCcw, FiShoppingCart,
  FiTrendingUp, FiDollarSign, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import VendorSelect from '../../components/VendorSelect';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import { addPrintBrand } from '../../utils/printBranding';

// ── Constants ──
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];
const STATUS_OPTIONS = ['All', 'Draft', 'Posted', 'Paid', 'Partial', 'Cancelled'];

const formatAmountLocal = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

// ── Component ──

const PartyPurchaseSummary = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';

  // Data states
  const [transactions, setTransactions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [statusFilter, setStatusFilter] = useState('All');
  const [taxFilter, setTaxFilter] = useState('both');
  const [minAmount, setMinAmount] = useState('');

  // Sort states
  const [sortColumn, setSortColumn] = useState('netPurchases');
  const [sortDirection, setSortDirection] = useState('desc');

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // ── Load Vendors ──
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(ref, orderBy('vendorName'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  // ── Load Purchase Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const ref = collection(db, 'companies', activeCompany.id, 'transactions');

      const conditions = [
        where('tranTypeCode', 'in', ['PI', 'NPI', 'PR', 'NPR']),
        where('financialYearId', '==', selectedFYId)
      ];
      // Server-side date bounds (Firebase cost): fetch only the picked range
      // instead of the whole FY. Empty pickers = whole FY (old behavior).
      // Client-side date filter below stays as a harmless second pass.
      if (dateFrom) conditions.push(where('docDate', '>=', dateFrom));
      if (dateTo) conditions.push(where('docDate', '<=', dateTo));

      const q = query(ref, ...conditions);
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });
      data.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(data);
    } catch (error) {
      console.error('Error loading purchase transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch, dateFrom, dateTo]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);
  useEffect(() => { loadVendors(); }, [loadVendors]);
  useEffect(() => {
    if (selectedFYId) loadTransactions();
  }, [loadTransactions, selectedFYId]);

  // ── Filtered Transactions ──
  const filteredData = useMemo(() => {
    let data = [...transactions];

    if (dateFrom) {
      data = data.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      data = data.filter(t => (t.docDate || '') <= dateTo);
    }
    if (selectedVendor !== 'all') {
      data = data.filter(t => t.partyId === selectedVendor);
    }
    if (statusFilter !== 'All') {
      data = data.filter(t => t.status === statusFilter);
    }
    if (taxFilter === 'tax') {
      data = data.filter(t => !['NPI', 'NPR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      data = data.filter(t => !['PI', 'PR'].includes(t.tranTypeCode));
    }

    return data;
  }, [transactions, dateFrom, dateTo, selectedVendor, statusFilter, taxFilter]);

  // ── Vendor Map: build lookup from vendors collection ──
  const vendorMap = useMemo(() => {
    const map = {};
    vendors.forEach(v => {
      map[v.id] = v;
    });
    return map;
  }, [vendors]);

  // ── Party Summary Data (grouped by partyId) ──
  const partySummaryData = useMemo(() => {
    const partyMap = {};

    filteredData.forEach(t => {
      const partyId = t.partyId || 'unknown';
      if (!partyMap[partyId]) {
        const vendor = vendorMap[partyId];
        partyMap[partyId] = {
          partyId,
          vendorCode: vendor?.vendorCode || '-',
          vendorName: t.partyName || vendor?.vendorName || 'Unknown',
          invoiceCount: 0,
          grossPurchases: 0,
          returns: 0,
          discount: 0,
          gst: 0,
          wht: 0,
          netPurchases: 0
        };
      }

      const entry = partyMap[partyId];
      if (['PI', 'NPI'].includes(t.tranTypeCode)) {
        entry.invoiceCount += 1;
        entry.grossPurchases += (t.totalAmount || 0);
        entry.discount += (t.totalDiscount || 0);
        entry.gst += (t.totalGST || 0);
        entry.wht += (t.totalWHT || 0);
        entry.netPurchases += (t.netAmount || 0);
      } else if (['PR', 'NPR'].includes(t.tranTypeCode)) {
        entry.returns += (t.netAmount || 0);
        entry.netPurchases -= (t.netAmount || 0);
      }
    });

    let result = Object.values(partyMap);

    // Apply min amount filter
    if (minAmount && !isNaN(parseFloat(minAmount))) {
      const minAmt = parseFloat(minAmount);
      result = result.filter(p => p.netPurchases >= minAmt);
    }

    return result;
  }, [filteredData, vendorMap, minAmount]);

  // ── Sorted Party Data ──
  const sortedData = useMemo(() => {
    const data = [...partySummaryData];
    data.sort((a, b) => {
      let valA, valB;
      switch (sortColumn) {
        case 'vendorCode':
          valA = (a.vendorCode || '').toLowerCase();
          valB = (b.vendorCode || '').toLowerCase();
          break;
        case 'vendorName':
          valA = (a.vendorName || '').toLowerCase();
          valB = (b.vendorName || '').toLowerCase();
          break;
        case 'invoiceCount':
          valA = a.invoiceCount || 0;
          valB = b.invoiceCount || 0;
          break;
        case 'grossPurchases':
          valA = a.grossPurchases || 0;
          valB = b.grossPurchases || 0;
          break;
        case 'returns':
          valA = a.returns || 0;
          valB = b.returns || 0;
          break;
        case 'discount':
          valA = a.discount || 0;
          valB = b.discount || 0;
          break;
        case 'gst':
          valA = a.gst || 0;
          valB = b.gst || 0;
          break;
        case 'wht':
          valA = a.wht || 0;
          valB = b.wht || 0;
          break;
        case 'netPurchases':
          valA = a.netPurchases || 0;
          valB = b.netPurchases || 0;
          break;
        case 'pctOfTotal':
          valA = a.netPurchases || 0;
          valB = b.netPurchases || 0;
          break;
        default:
          valA = a.netPurchases || 0;
          valB = b.netPurchases || 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [partySummaryData, sortColumn, sortDirection]);

  // ── Summary Calculations ──
  const summary = useMemo(() => {
    const uniqueVendors = new Set(filteredData.map(t => t.partyId)).size;
    const piTransactions = filteredData.filter(t => ['PI', 'NPI'].includes(t.tranTypeCode));
    const prTransactions = filteredData.filter(t => ['PR', 'NPR'].includes(t.tranTypeCode));

    const totalPurchases = piTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const totalReturns = prTransactions.reduce((sum, t) => sum + (t.netAmount || 0), 0);
    const netPurchases = totalPurchases - totalReturns;

    return {
      totalVendors: uniqueVendors,
      totalPurchases,
      totalReturns,
      netPurchases,
      piCount: piTransactions.length,
      prCount: prTransactions.length
    };
  }, [filteredData]);

  // ── Grand Total for % of Total ──
  const grandTotalNet = useMemo(() => {
    return sortedData.reduce((sum, p) => sum + p.netPurchases, 0);
  }, [sortedData]);

  // ── Chart Data: Top 15 Vendors (Horizontal Bar) ──
  const vendorBarChartData = useMemo(() => {
    return [...partySummaryData]
      .sort((a, b) => b.netPurchases - a.netPurchases)
      .slice(0, 15)
      .map(item => ({
        vendor: item.vendorName.length > 20 ? item.vendorName.substring(0, 20) + '...' : item.vendorName,
        amount: item.netPurchases
      }));
  }, [partySummaryData]);

  // ── Chart Data: Vendor Distribution (Pie) ──
  const vendorPieChartData = useMemo(() => {
    const sorted = [...partySummaryData]
      .filter(p => p.netPurchases > 0)
      .sort((a, b) => b.netPurchases - a.netPurchases);

    if (sorted.length <= 6) {
      return sorted.map(p => ({ name: p.vendorName, value: p.netPurchases }));
    }

    const top5 = sorted.slice(0, 5).map(p => ({ name: p.vendorName, value: p.netPurchases }));
    const othersTotal = sorted.slice(5).reduce((sum, p) => sum + p.netPurchases, 0);
    return [...top5, { name: 'Others', value: othersTotal }];
  }, [partySummaryData]);

  // ── Table Totals ──
  const tableTotals = useMemo(() => {
    return {
      invoiceCount: sortedData.reduce((s, p) => s + p.invoiceCount, 0),
      grossPurchases: sortedData.reduce((s, p) => s + p.grossPurchases, 0),
      returns: sortedData.reduce((s, p) => s + p.returns, 0),
      discount: sortedData.reduce((s, p) => s + p.discount, 0),
      gst: sortedData.reduce((s, p) => s + p.gst, 0),
      wht: sortedData.reduce((s, p) => s + p.wht, 0),
      netPurchases: sortedData.reduce((s, p) => s + p.netPurchases, 0)
    };
  }, [sortedData]);

  // ── Sort Handler ──
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <FiArrowUp size={11} style={{ marginLeft: 4 }} />
      : <FiArrowDown size={11} style={{ marginLeft: 4 }} />;
  };

  // ── Reset Filters ──
  const handleResetFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedVendor('all');
    setStatusFilter('All');
    setMinAmount('');
    if (activeFinancialYear?.id) {
      setSelectedFYId(activeFinancialYear.id);
    }
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Party Purchase Summary Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}\n`;
    csv += `Status: ${statusFilter}\n\n`;

    csv += `#,Vendor Code,Vendor Name,Invoice Count,Gross Purchases,Returns,Discount,${taxLabel},WHT,Net Purchases,% of Total\n`;

    sortedData.forEach((p, idx) => {
      const pct = grandTotalNet > 0 ? ((p.netPurchases / grandTotalNet) * 100).toFixed(1) : '0.0';
      csv += `${idx + 1},`;
      csv += `"${(p.vendorCode || '').replace(/"/g, '""')}",`;
      csv += `"${(p.vendorName || '').replace(/"/g, '""')}",`;
      csv += `${p.invoiceCount},`;
      csv += `${p.grossPurchases.toFixed(2)},`;
      csv += `${p.returns.toFixed(2)},`;
      csv += `${p.discount.toFixed(2)},`;
      csv += `${p.gst.toFixed(2)},`;
      csv += `${p.wht.toFixed(2)},`;
      csv += `${p.netPurchases.toFixed(2)},`;
      csv += `${pct}%\n`;
    });

    csv += `\nTOTAL,,${sortedData.length} vendors,${tableTotals.invoiceCount},`;
    csv += `${tableTotals.grossPurchases.toFixed(2)},${tableTotals.returns.toFixed(2)},`;
    csv += `${tableTotals.discount.toFixed(2)},${tableTotals.gst.toFixed(2)},`;
    csv += `${tableTotals.wht.toFixed(2)},${tableTotals.netPurchases.toFixed(2)},100%\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Party_Purchase_Summary_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Print ──
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Party Purchase Summary</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:20px}
        .summary-row{display:flex;gap:20px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{border:1px solid #ddd;border-radius:6px;padding:10px 16px;min-width:140px}
        .summary-box .label{font-size:11px;color:#666;text-transform:uppercase}
        .summary-box .value{font-size:18px;font-weight:600;color:#1A1A1A}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#800020;color:white;padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase}
        th.right{text-align:right}
        th.center{text-align:center}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:12px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        td.center{text-align:center}
        .totals-row{background:#f5f5f5;font-weight:700}
        .totals-row td{border-top:2px solid #800020;padding:10px}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Party Purchase Summary Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'} |
        Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'} |
        Status: ${statusFilter} |
        Total Vendors: ${sortedData.length}
      </div>
      <div class="summary-row">
        <div class="summary-box"><div class="label">Total Vendors</div><div class="value">${formatNumber(summary.totalVendors)}</div></div>
        <div class="summary-box"><div class="label">Total Purchases</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalPurchases)}</div></div>
        <div class="summary-box"><div class="label">Total Returns</div><div class="value">${currencySymbol}${formatAmountLocal(summary.totalReturns)}</div></div>
        <div class="summary-box"><div class="label">Net Purchases</div><div class="value">${currencySymbol}${formatAmountLocal(summary.netPurchases)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>#</th><th>Code</th><th>Vendor Name</th><th class="center">Invoices</th>
          <th class="right">Gross</th><th class="right">Returns</th><th class="right">Disc.</th>
          <th class="right">${taxLabel}</th><th class="right">WHT</th><th class="right">Net Purchases</th>
          <th class="right">% of Total</th>
        </tr></thead>
        <tbody>`);

    sortedData.forEach((p, idx) => {
      const pct = grandTotalNet > 0 ? ((p.netPurchases / grandTotalNet) * 100).toFixed(1) : '0.0';
      w.document.write(`
        <tr>
          <td>${idx + 1}</td>
          <td>${p.vendorCode || '-'}</td>
          <td>${p.vendorName || '-'}</td>
          <td class="center">${p.invoiceCount}</td>
          <td class="right">${formatAmountLocal(p.grossPurchases)}</td>
          <td class="right">${formatAmountLocal(p.returns)}</td>
          <td class="right">${formatAmountLocal(p.discount)}</td>
          <td class="right">${formatAmountLocal(p.gst)}</td>
          <td class="right">${formatAmountLocal(p.wht)}</td>
          <td class="right">${formatAmountLocal(p.netPurchases)}</td>
          <td class="right">${pct}%</td>
        </tr>`);
    });

    w.document.write(`
        <tr class="totals-row">
          <td colspan="3" style="text-align:right">TOTAL (${sortedData.length} vendors)</td>
          <td class="center">${tableTotals.invoiceCount}</td>
          <td class="right">${formatAmountLocal(tableTotals.grossPurchases)}</td>
          <td class="right">${formatAmountLocal(tableTotals.returns)}</td>
          <td class="right">${formatAmountLocal(tableTotals.discount)}</td>
          <td class="right">${formatAmountLocal(tableTotals.gst)}</td>
          <td class="right">${formatAmountLocal(tableTotals.wht)}</td>
          <td class="right">${formatAmountLocal(tableTotals.netPurchases)}</td>
          <td class="right">100%</td>
        </tr>
        </tbody></table>
        <div style="margin-top:30px;text-align:center;color:#999;font-size:11px">
          Generated on ${new Date().toLocaleString()} | AMAL ERP
        </div>
      </body></html>`);
    addPrintBrand(w);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Custom Tooltips ──
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{label}</ChartTooltipLabel>
        {payload.map((entry, i) => (
          <ChartTooltipRow key={i}>
            <ChartTooltipDot style={{ background: entry.color }} />
            <span>{entry.name}:</span>
            <strong>{formatCurrency(entry.value)}</strong>
          </ChartTooltipRow>
        ))}
      </ChartTooltipWrapper>
    );
  };

  const PieCustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0];
    const total = vendorPieChartData.reduce((s, d) => s + d.value, 0);
    const pct = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
    return (
      <ChartTooltipWrapper>
        <ChartTooltipLabel>{data.name}</ChartTooltipLabel>
        <ChartTooltipRow>
          <span>Amount:</span>
          <strong>{formatCurrency(data.value)}</strong>
        </ChartTooltipRow>
        <ChartTooltipRow>
          <span>Share:</span>
          <strong>{pct}%</strong>
        </ChartTooltipRow>
      </ChartTooltipWrapper>
    );
  };

  // ── Render ──
  return (
    <PageLayout pageTitle="Party Purchase Summary" breadcrumb="Reports / Purchase Reports / Party Purchase Summary">
      {/* Summary Cards */}
      <CardsGrid>
        <SummaryCard>
          <CardIcon color={colors.primary.main}>
            <FiUsers />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Vendors</CardLabel>
            <CardValue>{formatNumber(summary.totalVendors)}</CardValue>
            <CardSub>Active vendors</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.success.main}>
            <FiShoppingCart />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Purchases</CardLabel>
            <CardValue>{formatCurrency(summary.totalPurchases)}</CardValue>
            <CardSub>{summary.piCount} invoice{summary.piCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.error.main}>
            <FiRotateCcw />
          </CardIcon>
          <CardInfo>
            <CardLabel>Total Returns</CardLabel>
            <CardValue>{formatCurrency(summary.totalReturns)}</CardValue>
            <CardSub>{summary.prCount} return{summary.prCount !== 1 ? 's' : ''}</CardSub>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <CardIcon color={colors.warning.main}>
            <FiTrendingUp />
          </CardIcon>
          <CardInfo>
            <CardLabel>Net Purchases</CardLabel>
            <CardValue>{formatCurrency(summary.netPurchases)}</CardValue>
            <CardSub>Purchases - Returns</CardSub>
          </CardInfo>
        </SummaryCard>
      </CardsGrid>

      {/* Filters */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select
              value={selectedFYId}
              onChange={(e) => setSelectedFYId(e.target.value)}
            >
              <option value="">-- Select Year --</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Vendor</Label>
            <VendorSelect
              vendors={vendors}
              selectedVendorId={selectedVendor}
              onChange={setSelectedVendor}
              allOption={{ value: 'all', label: 'All Vendors' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={(e) => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Min Amount</Label>
            <AmountInput
              type="number"
              placeholder="0"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              min="0"
            />
          </FilterGroup>

          <ButtonGroup>
            <Button onClick={handleResetFilters} title="Reset Filters">
              <FiRotateCcw /> Reset
            </Button>
            <Button onClick={handleExport}>
              <FiDownload /> CSV
            </Button>
            <Button onClick={handlePrint}>
              <FiPrinter /> Print
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* Data Table */}
      <ContentCard>
        <TableHeader>
          <TableHeaderTitle>
            Vendor Purchase Summary
            <TableCount>{sortedData.length} vendor{sortedData.length !== 1 ? 's' : ''}</TableCount>
          </TableHeaderTitle>
        </TableHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <LoadingSpinner />
              <EmptyText>Loading party purchase data...</EmptyText>
            </EmptyState>
          ) : sortedData.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiUsers /></EmptyIcon>
              <EmptyText>
                {transactions.length === 0
                  ? 'No purchase transactions found for this financial year'
                  : 'No vendors match the selected filters'}
              </EmptyText>
              <EmptySubText>Try adjusting your filters or selecting a different financial year</EmptySubText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>#</Th>
                  <Th sortable onClick={() => handleSort('vendorCode')}>
                    Vendor Code {renderSortIcon('vendorCode')}
                  </Th>
                  <Th sortable onClick={() => handleSort('vendorName')}>
                    Vendor Name {renderSortIcon('vendorName')}
                  </Th>
                  <Th align="center" sortable onClick={() => handleSort('invoiceCount')}>
                    Invoices {renderSortIcon('invoiceCount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('grossPurchases')}>
                    Gross Purchases {renderSortIcon('grossPurchases')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('returns')}>
                    Returns {renderSortIcon('returns')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('discount')}>
                    Discount {renderSortIcon('discount')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('gst')}>
                    {taxLabel} {renderSortIcon('gst')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('wht')}>
                    WHT {renderSortIcon('wht')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('netPurchases')}>
                    Net Purchases {renderSortIcon('netPurchases')}
                  </Th>
                  <Th align="right" sortable onClick={() => handleSort('pctOfTotal')}>
                    % of Total {renderSortIcon('pctOfTotal')}
                  </Th>
                </tr>
              </Thead>
              <Tbody>
                {sortedData.map((p, idx) => {
                  const pct = grandTotalNet > 0 ? ((p.netPurchases / grandTotalNet) * 100).toFixed(1) : '0.0';
                  return (
                    <Tr key={p.partyId}>
                      <Td>{idx + 1}</Td>
                      <Td>
                        <VendorCode>{p.vendorCode || '-'}</VendorCode>
                      </Td>
                      <Td title={p.vendorName}>
                        <VendorLink onClick={() => navigate('/reports/purchase-register', { state: { filterVendorId: p.vendorId || p.partyId, filterVendorName: p.vendorName || p.partyName } })}>
                          {p.vendorName || '-'}
                        </VendorLink>
                      </Td>
                      <Td align="center">{p.invoiceCount}</Td>
                      <AmountTd align="right">{formatAmountLocal(p.grossPurchases)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.returns)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.discount)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.gst)}</AmountTd>
                      <AmountTd align="right">{formatAmountLocal(p.wht)}</AmountTd>
                      <AmountTd align="right" $highlight>
                        {formatAmountLocal(p.netPurchases)}
                      </AmountTd>
                      <Td align="right">
                        <PctBadge>{pct}%</PctBadge>
                      </Td>
                    </Tr>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>
                    TOTAL ({sortedData.length} vendors)
                  </Td>
                  <Td align="center">{tableTotals.invoiceCount}</Td>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.grossPurchases)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.returns)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.discount)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.gst)}</AmountTd>
                  <AmountTd align="right">{formatAmountLocal(tableTotals.wht)}</AmountTd>
                  <AmountTd align="right" $highlight>{formatAmountLocal(tableTotals.netPurchases)}</AmountTd>
                  <Td align="right" style={{ fontWeight: 700 }}>100%</Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {!loading && partySummaryData.length > 0 && (
        <ChartsGrid>
          {/* Horizontal Bar Chart: Top 15 Vendors */}
          <ChartCard>
            <ChartTitle>Top 15 Vendors by Net Purchases</ChartTitle>
            {vendorBarChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={vendorBarChartData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => formatCurrency(v, { compact: true })}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendor"
                    tick={{ fontSize: 10, fill: colors.text.secondary }}
                    axisLine={false}
                    tickLine={false}
                    width={150}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="amount" name="Net Purchases" fill="#800020" radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>

          {/* Pie Chart: Vendor Distribution */}
          <ChartCard>
            <ChartTitle>Purchase Distribution by Vendor</ChartTitle>
            {vendorPieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={vendorPieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name.length > 12 ? name.substring(0, 12) + '..' : name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={{ stroke: colors.text.secondary, strokeWidth: 1 }}
                  >
                    {vendorPieChartData.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<PieCustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No vendor data available</ChartEmpty>
            )}
          </ChartCard>
        </ChartsGrid>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════════════════════
//  Styled Components
// ══════════════════════════════════════════════════════════════

// ── Summary Cards ──

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 600;
`;

const CardValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 2px;
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 140px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const AmountInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  background: ${colors.neutral[100]};
  color: ${colors.text.primary};
  white-space: nowrap;

  &:hover {
    background: ${colors.neutral[200]};
  }
`;

// ── Charts ──

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

const ChartEmpty = styled.div`
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipWrapper = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  box-shadow: ${colors.shadows.md};
  padding: 10px 14px;
  font-size: 12px;
`;

const ChartTooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
  font-size: 12px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
    margin-left: auto;
  }
`;

const ChartTooltipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const TableHeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 3px 10px;
  border-radius: 12px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 11px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.sortable && `
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.primary.main}08;
  border-top: 2px solid ${colors.primary.main};

  td {
    font-weight: 700 !important;
    font-size: 13px !important;
    padding: 12px 14px !important;
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  color: ${props => props.$highlight ? colors.text.primary : colors.text.secondary};
  font-weight: ${props => props.$highlight ? 600 : 400};
`;

const VendorCode = styled.span`
  color: ${colors.primary.main};
  font-weight: 600;
  font-size: 12px;
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const VendorLink = styled.span`
  color: #1a73e8;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.dark};
    text-decoration: underline;
  }
`;

const PctBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  font-family: 'SF Mono', 'Consolas', monospace;
`;

// ── Empty / Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${colors.text.primary};
`;

const EmptySubText = styled.p`
  font-size: 13px;
  color: ${colors.text.tertiary};
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${colors.neutral[200]};
  border-top: 3px solid ${colors.primary.main};
  border-radius: 50%;
  margin: 0 auto 16px auto;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export default PartyPurchaseSummary;
